import React from "react";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  DownloadIcon,
  LockIcon,
  SendIcon,
  UnlockIcon,
} from "lucide-react";
import type { PayrollUiPayPeriod } from "../../types";

type PayPeriodStatusBarProps = {
  period: PayrollUiPayPeriod;
  onActionClick: (action: string) => void;
};

export function PayPeriodStatusBar({
  period,
  onActionClick,
}: PayPeriodStatusBarProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "bg-muted text-text-secondary border-border";
      case "In Progress":
        return "bg-info-background text-primary border-primary/30";
      case "Submitted for Approval":
        return "bg-warning-background text-[#D97706] border-[#D97706]/30";
      case "Approved":
        return "bg-success-background text-jungle border-aquamarine/30";
      case "Exported":
        return "bg-primary/10 text-primary border-primary/30";
      case "Locked":
        return "bg-muted text-iron-grey border-border-strong";
      default:
        return "bg-muted text-text-secondary border-border";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Draft":
      case "In Progress":
        return <ClockIcon className="h-3.5 w-3.5" />;
      case "Submitted for Approval":
        return <AlertCircleIcon className="h-3.5 w-3.5" />;
      case "Approved":
      case "Exported":
        return <CheckCircleIcon className="h-3.5 w-3.5" />;
      case "Locked":
        return <LockIcon className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const progressPercentage =
    period.totalEmployees > 0
      ? Math.round((period.employeesComplete / period.totalEmployees) * 100)
      : 0;
  const daysUntilExport = Math.ceil(
    (new Date(period.exportDeadline).getTime() - new Date().getTime()) /
      (1000 * 3600 * 24),
  );
  const isOverdue = daysUntilExport < 0;

  return (
    <div className="mb-6 flex flex-col justify-between gap-6 rounded-[12px] border border-border bg-surface-light p-5 shadow-sm md:flex-row md:items-center">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="min-w-0 font-heading text-xl font-semibold text-iron-grey">
            {period.label}
          </h2>
          <span
            className={`inline-flex shrink-0 cursor-help items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${getStatusColor(period.status)}`}
            title="Click to view history"
          >
            {getStatusIcon(period.status)}
            {period.status}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-4 font-body text-sm text-text-secondary">
          <div className="flex min-w-0 items-center gap-1.5">
            <CalendarIcon className="h-4 w-4" />
            <span>
              Export deadline:{" "}
              <span
                className={`font-medium ${
                  isOverdue ? "text-strawberry" : "text-iron-grey"
                }`}
              >
                {new Date(period.exportDeadline).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </span>
            {daysUntilExport >= 0 ? (
              <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-xs text-text-secondary">
                {daysUntilExport} days left
              </span>
            ) : (
              <span className="ml-1 rounded bg-error-background px-1.5 py-0.5 text-xs text-strawberry">
                {Math.abs(daysUntilExport)} days overdue
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-w-0 flex-col items-start gap-6 sm:flex-row sm:items-center md:gap-8">
        <div className="flex min-w-0 w-full flex-col gap-1.5 sm:w-48">
          <div className="flex items-center justify-between font-body text-sm">
            <span className="text-text-secondary">Completion</span>
            <span
              className="cursor-pointer font-medium text-iron-grey transition-colors hover:text-primary"
              title="View incomplete"
            >
              {period.employeesComplete} / {period.totalEmployees}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                progressPercentage === 100 ? "bg-aquamarine" : "bg-primary"
              }`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="font-body text-[11px] uppercase tracking-wider text-text-secondary">
            Total Gross
          </span>
          <span className="font-body text-2xl font-bold text-iron-grey">
            {formatCurrency(period.totalGross)}
          </span>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {period.status === "In Progress" ? (
            <button
              onClick={() => onActionClick("submit")}
              disabled={progressPercentage < 100}
              data-testid="payroll-period-submit"
              className="flex min-w-0 items-center gap-2 rounded-[6px] bg-primary px-4 py-2 text-left font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              title={progressPercentage < 100 ? "All employees must be complete" : ""}
            >
              <SendIcon className="h-4 w-4" />
              Submit for Approval
            </button>
          ) : null}

          {period.status === "Approved" ||
          period.status === "Draft" ||
          period.status === "In Progress" ? (
            <button
              onClick={() => onActionClick("export")}
              data-testid="payroll-period-export"
              className="flex min-w-0 items-center gap-2 rounded-[6px] border border-border bg-surface-light px-4 py-2 text-left font-body text-sm font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted"
            >
              <DownloadIcon className="h-4 w-4 text-text-secondary" />
              Export CSV
            </button>
          ) : null}

          {period.status === "Exported" || period.status === "Locked" ? (
            <button
              onClick={() => onActionClick("unlock")}
              data-testid="payroll-period-unlock"
              className="flex min-w-0 items-center gap-2 rounded-[6px] border border-border bg-surface-light px-4 py-2 text-left font-body text-sm font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted"
            >
              <UnlockIcon className="h-4 w-4 text-text-secondary" />
              Unlock Period
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  );
}
